import { useEffect, useState } from 'react'
import { bytes, eta, kecepatan } from '../../lib/format'
import { Button } from '../Button'
import { Icon } from '../Icon'
import type { ItemUnggah } from './useUploader'
import css from './UploadPanel.module.css'

/**
 * Panel antrean unggahan.
 *
 * Mengapung di sudut layar dan tidak menghalangi pekerjaan lain — versi lama
 * memuat ulang halaman satu detik setelah unggahan selesai, yang memutus
 * unggahan lain yang masih berjalan.
 */
export function UploadPanel({
  antrean,
  onBatal,
  onBersihkan,
  adaYangBerjalan,
}: {
  antrean: ItemUnggah[]
  onBatal: (id: string) => void
  onBersihkan: () => void
  adaYangBerjalan: boolean
}) {
  /**
   * Cegah halaman ditutup saat unggahan masih berjalan.
   *
   * Peramban modern mengabaikan teks kustom dan menampilkan pesan bawaannya
   * sendiri, tetapi dialog konfirmasinya tetap muncul — itu yang penting.
   */
  useEffect(() => {
    if (!adaYangBerjalan) return
    const jaga = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', jaga)
    return () => window.removeEventListener('beforeunload', jaga)
  }, [adaYangBerjalan])

  if (antrean.length === 0) return null

  const selesai = antrean.filter((i) => i.status === 'selesai').length
  const gagal = antrean.filter((i) => i.status === 'gagal').length
  const aktif = antrean.filter((i) => i.status === 'berjalan' || i.status === 'antre').length

  return (
    <div className={css.panel} role="status" aria-live="polite">
      <div className={css.header}>
        <span className={css.headerTitle}>
          {aktif > 0
            ? `Mengunggah ${aktif} berkas`
            : gagal > 0
              ? `${gagal} unggahan gagal`
              : `${selesai} berkas terunggah`}
        </span>
        {!adaYangBerjalan && (
          <Button variant="ghost" size="sm" icon="close" onClick={onBersihkan} aria-label="Tutup panel unggahan" />
        )}
      </div>

      <div className={css.list}>
        {antrean.map((item) => (
          <div className={css.item} key={item.id}>
            <span className={`${css.itemIcon} ${css[item.status]}`}>
              <Icon
                name={
                  item.status === 'selesai'
                    ? 'success'
                    : item.status === 'gagal'
                      ? 'error'
                      : item.status === 'dibatalkan'
                        ? 'close'
                        : 'upload'
                }
                size={16}
              />
            </span>

            <div className={css.itemBody}>
              <div className={`${css.itemName} truncate`} title={item.file.name}>
                {item.namaAkhir ?? item.file.name}
              </div>

              {item.status === 'berjalan' && (
                <>
                  <div className={css.track}>
                    <div className={css.fill} style={{ width: `${item.progres}%` }} />
                  </div>
                  <div className={css.itemMeta}>
                    {item.progres}% · {kecepatan(item.kecepatan)}
                    {item.sisa > 0 && ` · ${eta(item.sisa)}`}
                  </div>
                </>
              )}

              {item.status === 'antre' && <div className={css.itemMeta}>Menunggu giliran</div>}

              {item.status === 'selesai' && (
                <div className={css.itemMeta}>
                  {bytes(item.file.size)}
                  {item.namaAkhir && item.namaAkhir !== item.file.name && (
                    <> · disimpan sebagai {item.namaAkhir}</>
                  )}
                </div>
              )}

              {item.status === 'gagal' && <div className={css.itemError}>{item.galat}</div>}

              {item.status === 'dibatalkan' && <div className={css.itemMeta}>Dibatalkan</div>}
            </div>

            {(item.status === 'berjalan' || item.status === 'antre') && (
              <Button
                variant="ghost"
                size="sm"
                icon="close"
                onClick={() => onBatal(item.id)}
                aria-label={`Batalkan unggahan ${item.file.name}`}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

/** Zona seret-lepas yang membungkus area penjelajah. */
export function DropZone({
  aktif,
  onFiles,
  children,
}: {
  aktif: boolean
  onFiles: (files: File[]) => void
  children: React.ReactNode
}) {
  const [seret, setSeret] = useState(false)

  if (!aktif) return <>{children}</>

  return (
    <div
      className={css.dropWrap}
      onDragOver={(e) => {
        // Wajib dicegah, kalau tidak peramban akan membuka berkasnya di tab ini.
        e.preventDefault()
        if (e.dataTransfer.types.includes('Files')) setSeret(true)
      }}
      onDragLeave={(e) => {
        // Hanya dianggap keluar bila kursor benar-benar meninggalkan wadahnya,
        // bukan sekadar berpindah ke elemen anak di dalamnya.
        if (!e.currentTarget.contains(e.relatedTarget as Node)) setSeret(false)
      }}
      onDrop={(e) => {
        e.preventDefault()
        setSeret(false)
        const files = Array.from(e.dataTransfer.files)
        if (files.length > 0) onFiles(files)
      }}
    >
      {children}
      {seret && (
        <div className={css.dropOverlay}>
          <Icon name="upload" size={34} />
          <span>Lepaskan berkas untuk mengunggah ke folder ini</span>
        </div>
      )}
    </div>
  )
}
